import {
	CHANGE_PASSWORD_FORM,
	useManagePasswordController,
} from '@dealstack/frontend/shared/components/AccountSettings/ManagePassword/useManagePasswordController';
import {
	DefaultBorderColor,
	ActiveBorderColor,
	Header,
} from '@dealstack/frontend/shared/components/AccountSettings/Header';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import {
	useTranslationAuth,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { FormProvider } from 'react-hook-form';
import { TextInput } from 'react-hook-form-mantine';
import { Text, Button } from '@mantine/core';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { NewPasswordInput } from '@dealstack/frontend/shared/components/NewPasswordInput/NewPasswordInput';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { VerificationCode } from '@dealstack/frontend/shared/components/VerificationCode/VerificationCode';
import type { SectionEditMode } from '@dealstack/frontend/shared/components/AccountSettings/AccountSettings.types';

interface Props extends SectionEditMode {
	email: string;
	canChangePassword: boolean;
	authenticationFactorId?: string;
}

export const ManagePassword = ({
	email,
	canChangePassword,
	authenticationFactorId,
	isEditModeEnabled,
	setIsEditModeEnabled,
}: Props) => {
	const tCommonUI = useTranslationCommonUi();
	const tAuth = useTranslationAuth();

	const managePasswordController = useManagePasswordController(email, authenticationFactorId, {
		isEditModeEnabled,
		setIsEditModeEnabled,
	});
	if (managePasswordController.isLoading) {
		return <FullscreenLoader name="pageLoading" />;
	}

	const title = managePasswordController.isEditModeEnabled
		? tCommonUI('profile.account_settings.password.edit_header')
		: tCommonUI('profile.account_settings.password.header');

	const action = managePasswordController.isEditModeEnabled
		? tCommonUI('profile.account_settings.cancel')
		: tCommonUI('profile.account_settings.password.change_password');

	return (
		<div className="flex flex-col">
			<Header
				mainTitle={title}
				action={action}
				onClick={managePasswordController.onHeaderClick}
				canPerformAction={canChangePassword}
				data-testid={TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_PASSWORD_BUTTON}
				isEditModeEnabled={managePasswordController.isEditModeEnabled}
			/>
			<div
				className="flex p-4 border-solid border-r border-b border-l rounded-b-lg"
				style={{
					borderColor: managePasswordController.isEditModeEnabled
						? ActiveBorderColor
						: DefaultBorderColor,
				}}
			>
				{managePasswordController.isEditModeEnabled ? (
					<FormProvider {...managePasswordController.passwordChangeForm}>
						<form
							onSubmit={managePasswordController.passwordChangeForm.handleSubmit(
								managePasswordController.onSubmit
							)}
							className="flex flex-col gap-6 w-full"
						>
							<div className="flex flex-col gap-4">
								<TextInput
									name={CHANGE_PASSWORD_FORM.CURRENT_PASSWORD}
									control={managePasswordController.passwordChangeForm.control}
									size="md"
									placeholder={tCommonUI(
										'profile.account_settings.password.current_password_placeholder'
									)}
									label={tCommonUI(
										'profile.account_settings.password.current_password'
									)}
									type={
										managePasswordController.showCurrentPassword
											? 'text'
											: 'password'
									}
									data-testid={
										TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_PASSWORD_CURRENT_PASSWORD
									}
									rightSection={
										<Icon
											variant={
												managePasswordController.showCurrentPassword
													? 'unview-v3'
													: 'view-v3'
											}
											onClick={() => {
												managePasswordController.setShowCurrentPassword(
													!managePasswordController.showCurrentPassword
												);
											}}
											size="xl"
										/>
									}
								/>
								<NewPasswordInput
									form={managePasswordController.passwordChangeForm}
									showPassword={managePasswordController.showPassword}
									setShowPassword={managePasswordController.setShowPassword}
									isPasswordValid={managePasswordController.isPasswordValid}
									strengthScore={managePasswordController.strengthScore}
									name={CHANGE_PASSWORD_FORM.NEW_PASSWORD}
									placeholder={tCommonUI(
										'profile.account_settings.password.new_password_placeholder'
									)}
									label={tCommonUI(
										'profile.account_settings.password.new_password'
									)}
									hint={tAuth('common-form.new_password.hint')}
								/>
								{managePasswordController.isTotpCodeShown && (
									<div
										className="flex flex-col gap-2"
										data-testid={
											TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_PASSWORD_TOTP_CODE
										}
									>
										<Text size="B6Medium" c="dark.8">
											{tCommonUI('profile.account_settings.totp.label')}
										</Text>
										<Text size="B6Medium" c="dark.0">
											{tCommonUI('profile.account_settings.totp.description')}
										</Text>
										<VerificationCode
											value={managePasswordController.totpCode}
											onChange={managePasswordController.setTotpCode}
											errorMessage={managePasswordController.totpCodeError}
											autoFocus={false}
										/>
									</div>
								)}
							</div>
							<Button
								type="submit"
								disabled={!managePasswordController.canSubmit}
								variant="primary"
								style={{ height: '36px', width: 'fit-content' }}
								data-testid={
									TEST_ELEMENT_IDS.ACCOUNT_SETTING_CHANGE_PASSWORD_SUBMIT_BUTTON
								}
							>
								{tCommonUI(
									'profile.account_settings.password.confirm_password_change'
								)}
							</Button>
						</form>
					</FormProvider>
				) : (
					<Text size="B6Medium" c="dark.8">
						••••••••
					</Text>
				)}
			</div>
		</div>
	);
};
